import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Flex } from '@theme-ui/components';

import { isNotNullish } from '@app/helpers/predicate';

import { getUpgradeVariant } from '../../helpers';
import type { PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants as MagentoProductVariant } from '../../hook/__generated__/PricingPageShopProduct';
import type { UpgradePath, UpgradeVariants } from '../../hook/useUpgradePaths';
import UpgradeLink from '../ProductUpgradeMessage/UpgradeLink';
import messages from './ProductUpgradeMessage.messages';

interface QualifiesForUpgradeMessageProps {
  variants: Array<MagentoProductVariant | null> | null;
  selectVariant: (value: MagentoProductVariant) => void;
  upgradeVariants: UpgradeVariants;
}

export const QualifiesForUpgradeMessage = ({
  variants,
  selectVariant,
  upgradeVariants,
}: QualifiesForUpgradeMessageProps) => {
  const upgradeVariant = useMemo(
    () => getUpgradeVariant(variants, upgradeVariants),
    [variants, upgradeVariants],
  );

  const hasUpgradeSdbsId = upgradeVariant && upgradeVariant.product?.sdbsId;
  const matchingUpgrade =
    hasUpgradeSdbsId && upgradeVariants?.[hasUpgradeSdbsId];

  if (upgradeVariant && matchingUpgrade) {
    return (
      <Box
        sx={{ flex: 1, fontWeight: 500 }}
        data-testid="qualifies-for-upgrade-message"
        mb={3}
      >
        <FormattedMessage
          {...messages.qualifiesForUpgrade}
          values={{
            baseProduct: matchingUpgrade.baseProduct.title,
            UpgradeButton: (
              <UpgradeLink
                selectVariant={selectVariant}
                upgradeVariant={upgradeVariant}
              />
            ),
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      data-testid="no-qualifying-products-on-account"
      sx={{ fontWeight: 500, mb: 3 }}
    >
      <FormattedMessage {...messages.userUnqualified} />
    </Box>
  );
};

export const QualifiesForVariantMessage = ({
  matchingUpgrade,
}: {
  matchingUpgrade: UpgradePath;
}) => {
  return (
    <Flex
      sx={{ justifyContent: 'space-between', flexWrap: 'wrap', mb: 3 }}
      data-testid="upgrade-message-qualifies-for-variant"
    >
      <Box sx={{ flex: 1, fontWeight: 500 }}>
        <FormattedMessage
          {...messages.qualifiesForVariant}
          values={{
            baseProduct: matchingUpgrade.baseProduct.title,
          }}
        />
      </Box>
    </Flex>
  );
};

const QualifyingProductMessage = ({
  selectedVariant,
  selectVariant,
  variants,
  upgradeVariants,
}: {
  selectedVariant: MagentoProductVariant | null;
  selectVariant: (value: MagentoProductVariant) => void;
  variants: Array<MagentoProductVariant | null> | null;
  matchingUpgrade?: UpgradePath;
  upgradeVariants: UpgradeVariants;
}) => {
  const sdbsId =
    selectedVariant?.product?.sdbsId ?? selectedVariant?.product?.sdbsId;

  const matchingUpgrade = isNotNullish(sdbsId)
    ? upgradeVariants[Number.parseInt(sdbsId)]
    : undefined;

  if (matchingUpgrade) {
    return <QualifiesForVariantMessage matchingUpgrade={matchingUpgrade} />;
  }

  return (
    <QualifiesForUpgradeMessage
      variants={variants}
      selectVariant={selectVariant}
      upgradeVariants={upgradeVariants}
    />
  );
};

export default QualifyingProductMessage;
